import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import { useRefreshTokenMutation } from '../../store/api/auth';
import { getSharedConstants } from '../../store/baseQuery';
import type { AuthUser } from '../../store/types';
import { useStomp } from '../StompProvider/hooks';

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  operatorId: string | null;
  // Set user/token from a fresh token right away, without a re-render.
  applyToken: (_token: string) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  operatorId: null,
  applyToken: () => {},
});

export type JwtToken = {
  exp: number;
  User: AuthUser;
};

const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { APP_TOKEN_KEY } = getSharedConstants();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [refresh] = useRefreshTokenMutation();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [operatorId, setOperatorId] = useState<string | null>(null);
  const { connect } = useStomp();

  const onlyOneTime = useRef(true);

  const storedToken = localStorage.getItem(APP_TOKEN_KEY);

  // Decode the token and set the user right away.
  const applyToken = useCallback((nextToken: string) => {
    try {
      const decodedToken = jwtDecode<JwtToken>(nextToken);
      setToken(nextToken);
      setUser(decodedToken.User);
      setOperatorId(decodedToken.User.extraData.operatorId || null);
      onlyOneTime.current = false;
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }, []);

  useEffect(() => {
    if (storedToken && onlyOneTime.current) {
      connect(storedToken);
      applyToken(storedToken);
    }
  }, [connect, storedToken, applyToken]);

  useEffect(() => {
    if (!localStorage.getItem(APP_TOKEN_KEY) || timerRef.current) {
      return;
    }
    timerRef.current = setInterval(async () => {
      // Re-read the token every tick — a previous refresh may have
      // replaced it. Decoding a token captured at mount would keep
      // reporting the old expiry and refresh on every tick forever.
      const current = localStorage.getItem(APP_TOKEN_KEY);
      if (!current) {
        return;
      }
      try {
        const decoded = jwtDecode<JwtToken>(current);
        const tokenExpires = Number(decoded.exp);
        const now = Math.round(Date.now() / 1000);
        if (tokenExpires - now < 60) {
          const data = await refresh().unwrap();
          if (data.status === 'SUCCESS' && data.data.token) {
            localStorage.setItem(APP_TOKEN_KEY, data.data.token);
            applyToken(data.data.token);
          }
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
      }
    }, 60000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [APP_TOKEN_KEY, refresh, token, applyToken]);

  return (
    <AuthContext.Provider value={{ token, user, operatorId, applyToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
